import {Component, CUSTOM_ELEMENTS_SCHEMA, effect, input, model, output, signal, viewChild} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {WjInputModule} from '@mescius/wijmo.angular2.input';
import {ColumnDeclaration, Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {AppPermissionsByLobRow, AppRoleAndPermissions} from '../../security/app-role-and-permissions';
import {DataType} from '@mescius/wijmo';
import {KeyNamePair} from '../app-config-matrix/app-config-matrix.component';
import {CellType, FlexGrid, FormatItemEventArgs} from '@mescius/wijmo.grid';
import {ArrayUtils} from '../../lib/arrays';
import {AccessControlService} from '../../security/access-control.service';

type AppPermissionsByLobRowWithLobName = AppPermissionsByLobRow & { lobName: string };

@Component({
    selector: 'app-admin-user-permissions',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        WjGridModule,
        WjInputModule
    ],
    templateUrl: './admin-user-permissions.component.html',
    styleUrl: './admin-user-permissions.component.scss',
})
export class AdminUserPermissionsComponent {

    offices = input<KeyNamePair[]>([]);
    lobs = input<KeyNamePair[]>([]);
    appRoleAndPermissions = model<AppRoleAndPermissions>();

    onApply = output<AppRoleAndPermissions>();

    protected readonly isDirty = signal<boolean>(false);

    protected readonly appRoles = AccessControlService.AppRoles;
    protected readonly Wijmo = Wijmo;
    private readonly rolesGrid = viewChild<WjFlexGrid>('rolesGrid');

    protected readonly userRolePermissionsForm = new FormGroup({
        roles: new FormControl<typeof AccessControlService.AppRoles>( [{ name: 'Developer and Operations', key: 'devops' }], { nonNullable: true }),
    });

    constructor() {
        effect(() => this.applyInputToForm());
    }

    protected onRolesGridInit(rolesGrid: WjFlexGrid) {
        WjGridUtils.addColumnGroups(rolesGrid, this.generateColumnDef());
        rolesGrid.formatItem.addHandler(this.gridFormatHandler.bind(this));
        rolesGrid.itemsSource = this.generateLobRows(this.appRoleAndPermissions());
        rolesGrid.cellEditEnded.addHandler(() => this.isDirty.set(true));
        WjGridUtils.unselectAll(rolesGrid);
    }

    private generateLobRows(appRolesAndPermissions: AppRoleAndPermissions | undefined): AppPermissionsByLobRowWithLobName[] {
        const rows = this.lobs().map(lob => {
            const row = this.offices().map(office => {
                const hasPermission = appRolesAndPermissions?.permissions?.[lob.key]?.[office.key] ?? false;
                return ([office.key, hasPermission]);
            });
            return { lobKey: lob.key, lobName: lob.name, ...Object.fromEntries(row) } as AppPermissionsByLobRowWithLobName;
        });

        return rows;
    }

    private generateColumnDef(): ColumnDeclaration[] {
        const officeColumns: ColumnDeclaration[] = this.offices().map(office => ({
            header: office.name,
            dataType: DataType.Boolean,
            binding: office.key,
            align: 'center',
            width: 70,
        }));

        const columnDef: ColumnDeclaration[] = [
            { header: "LoB", binding: "lobName", align: 'center', cssClass: 'grid-row-header', isReadOnly: true, width: 80 },
            { header: "Producing Office", align: 'center', columns: officeColumns }
        ];

        return columnDef;
    }

    private gridFormatHandler(s: FlexGrid, e: FormatItemEventArgs) {
        const cell = e.cell;

        const col = s.columns[e.col];
        const row = s.rows[e.row];

        if (e.panel === s.topLeftCells) {
        } else if (e.panel === s.rowHeaders) {
            cell.innerHTML = `${e.row + 1}`;
        } else if (e.panel === s.columnHeaders) {
            cell.classList.add('accent');
        }
        if (e.panel === s.cells) {
        }
    }

    onFlexGridDblClick(event: MouseEvent, rolesGrid: WjFlexGrid) {
        const ht = rolesGrid.hitTest(event);
        const roleData = rolesGrid.collectionView.items as any[];
        const officeKeys = this.offices().map(office => office.key) as string[];

        function boolVal(value: boolean): number { return value ? 1 : 0; }

        if (ht.cellType === CellType.ColumnHeader) {
            if (ht.row === 1 && ht.col > 0) {
                const officeKey = rolesGrid.columns[ht.col].binding!;
                const countTrues =  ArrayUtils.countIf(roleData, r => r[officeKey]);
                const maxNumTrues = roleData.length;
                const toggledValue = countTrues >= maxNumTrues / 2;
                roleData.forEach(row => row[officeKey] = !toggledValue);
                this.isDirty.set(true);
                rolesGrid.refresh();
            } else if (ht.col === 0) {
                const countTrues = ArrayUtils.sum(roleData, (n) => ArrayUtils.countIf(officeKeys, k => n[k]));
                const maxNumTrues = roleData.length * officeKeys.length;
                const toggledValue = countTrues >= maxNumTrues / 2;
                roleData.forEach((r) => officeKeys.forEach(k => r[k] = !toggledValue));
                this.isDirty.set(true);
                rolesGrid.refresh();
            }
        } else if (ht.cellType === CellType.Cell) {
            if (ht.col === 0) {
                const row = roleData[ht.row];
                const countTrues = ArrayUtils.countIf(officeKeys, k => row[k]);
                const maxNumTrues = officeKeys.length;
                const toggledValue = countTrues >= maxNumTrues / 2;
                officeKeys.forEach(k => row[k] = !toggledValue);
                this.isDirty.set(true);
                rolesGrid.refresh();
            }
        }
    }

    private applyInputToForm() {
        const current = this.appRoleAndPermissions()?.roles ?? [];
        const roles = AccessControlService.AppRoles.filter(r => current.includes(r.key));
        this.userRolePermissionsForm.patchValue({ roles: roles });
        this.userRolePermissionsForm.markAsUntouched();

        if (this.rolesGrid()) {
            this.rolesGrid()!.itemsSource = this.generateLobRows(this.appRoleAndPermissions());
        }

        this.isDirty.set(false);
    }

    protected onApplyRolePermissions() {
        const roles = this.userRolePermissionsForm.value.roles!.map(r => r.key);
        const permissionData = this.rolesGrid()!.collectionView.items as any[];
        const rolePermissions = AppRoleAndPermissions.fromLobList(roles, permissionData);
        this.appRoleAndPermissions.set(rolePermissions);
        this.onApply.emit(rolePermissions);

    }

    protected onUnsetRolePermissions() {
        this.applyInputToForm();
    }

    protected onCheckedRolesChanged() {
        this.isDirty.set(true);
    }

    public markDirty() {
        this.isDirty.set(true);
    }
}
